import { IPoolCreds } from "@services/getSettings";
import { SeparatorType } from "@store/me/actionTypes";

export const MAX_POOLS_COUNT = 3;
export const DEFAULT_SEPARATOR = ".";

export interface SettingFormValues {
  name: string;
  address_1: string;
  address_2: string;
  address_3: string;

  login_1: string;
  login_2: string;
  login_3: string;

  password_1: string;
  password_2: string;
  password_3: string;

  separator_1: SeparatorType;
  separator_2: SeparatorType;
  separator_3: SeparatorType;
}

export const isStratumProtocol = (address: string) => {
  const regex =
    /^(stratum\+tcp:\/\/([\w-]+)(\.([\w-]+))*):(([0-9]{1,4})|([1-5][0-9]{4})|(6[0-4][0-9]{3})|(65[0-4][0-9]{2})|(655[0-2][0-9])|(6553[0-5]))(#xnsub)?$/;

  return regex.test(address);
};

export const formValuesToCreds = (
  formValues: SettingFormValues
): { name: string; Pools: IPoolCreds[] } => {
  return {
    name: formValues.name,
    Pools: Object.keys(formValues).filter(key => key.match(/^address/) && formValues[key as keyof typeof formValues]).map(key => {
      const index = key.match(/\d$/)?.[0];
      return {
        address: formValues[`address_${index}` as keyof typeof formValues],
        login: formValues[`login_${index}` as keyof typeof formValues],
        password: formValues[`password_${index}` as keyof typeof formValues],
        separator: formValues[`separator_${index}` as keyof typeof formValues] as SeparatorType,
      };
    }),
  };
};

interface GetPoolsToChangeParams {
  selectedPools?: IPoolCreds[];
  maxLength?: number;
  separator?: string;
}

export const getPoolsToChange = ({
  selectedPools,
  maxLength = 3,
  separator = DEFAULT_SEPARATOR,
}: GetPoolsToChangeParams): IPoolCreds[] => {

  const pools = selectedPools || [];
  const rest = maxLength - pools.length;

  const emptyPools: IPoolCreds[] = Array.from(
    { length: Math.max(0, rest) },
    () => ({
      address: "",
      login: "",
      password: "",
      separator: separator,
    })
  ) as IPoolCreds[];

  return [...pools, ...emptyPools];
};

export interface IPoolError {
  isFullEmpty: boolean;
  needAddress: boolean;
  needLogin: boolean;
  needPassword: boolean;
  isAddressCorrect: boolean;
  isCorrect: boolean;
}

export const INITIAL_POOL_ERROR: IPoolError = {
  isFullEmpty: true,
  needAddress: true,
  needLogin: true,
  needPassword: true,
  isAddressCorrect: false,
  isCorrect: false,
}



export type PoolBase = Omit<IPoolCreds, "is_default">

export interface PoolWithCheck extends PoolBase, IPoolError { };

export const EMPTY_POOL: PoolBase = {
  address: "",
  login: "",
  password: "",
  separator: DEFAULT_SEPARATOR,
}

export const INITIAL_POOL_WITH_CHECK: PoolWithCheck = {
  ...EMPTY_POOL,
  ...INITIAL_POOL_ERROR,
};

// нет проверки на разделитель! (separator)
export function checkPool(
  pool: PoolBase
): IPoolError {

  const result: IPoolError = {
    isFullEmpty: true,
    needAddress: true,
    needLogin: true,
    needPassword: true,
    isAddressCorrect: false,
    isCorrect: false,
  }
  result.needAddress = !Boolean(pool.address.trim());
  result.needLogin = !Boolean(pool.login.trim());
  result.needPassword = !Boolean(pool.password.trim());

  if (!result.needAddress) {
    result.isAddressCorrect = isStratumProtocol(pool.address.trim());
  }
  result.isFullEmpty = result.needAddress
    && result.needLogin
    && result.needPassword;
  result.isCorrect = !result.isFullEmpty
    && (!result.needAddress
      && !result.needLogin
      && !result.needPassword
      && result.isAddressCorrect
    );
  return result;
}
export interface PoolsWithCheck {
  readonly pool1: PoolWithCheck;
  readonly pool2: PoolWithCheck;
  readonly pool3: PoolWithCheck;
}

export function hasAnyError(pools: PoolsWithCheck): boolean {
  return [
    pools.pool1,
    pools.pool2,
    pools.pool3
  ].some(p => !p.isFullEmpty && !p.isCorrect)
}

export function withCheckError(pools: IPoolCreds[]): PoolsWithCheck {
  return {
    pool1: pools.length > 0 ? {
      ...pools[0],
      ...checkPool(pools[0]),
    } : {
      ...INITIAL_POOL_WITH_CHECK
    },
    pool2: pools.length > 1 ? {
      ...pools[1],
      ...checkPool(pools[1]),
    } : {
      ...INITIAL_POOL_WITH_CHECK
    },
    pool3: pools.length > 2 ? {
      ...pools[2],
      ...checkPool(pools[2]),
    } : {
      ...INITIAL_POOL_WITH_CHECK
    },
  }
}

export function convertFormValuesToPoolWithCheck(
  values: SettingFormValues
): PoolsWithCheck {

  const pool1: PoolBase = {
    address: values.address_1,
    login: values.login_1,
    password: values.password_1,
    separator: values.separator_1,
  };

  const pool2: PoolBase = {
    address: values.address_2,
    login: values.login_2,
    password: values.password_2,
    separator: values.separator_2,
  };

  const pool3: PoolBase = {
    address: values.address_3,
    login: values.login_3,
    password: values.password_3,
    separator: values.separator_3,
  };

  return {
    pool1: { ...pool1, ...checkPool(pool1) },
    pool2: { ...pool2, ...checkPool(pool2) },
    pool3: { ...pool3, ...checkPool(pool3) },
  }
}